/**
 * Web Form Submitted Event Component
 *
 * Renders a form submission event on the session timeline.
 * Header: Actor captured a submission from <FormBadge> on this web visit
 * Associated: Actor captured a submission from <FormBadge> on <CrmObjectBadge>
 * Sub-items: expandable list of field name/value pairs.
 */

import { Type01 } from '@untitledui/icons';
import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';

import { EntityBadge } from '@/lib/crm/components/badges/EntityBadge';
import { useOpenSlideoutOptional } from '@/lib/crm/hooks/useOpenSlideout';

import {
  ActorName,
  bold,
  InlineText,
  TimelineEvent,
} from '../components/shared';
import { SubjectReference } from '../components/SubjectReference';
import { useViewStateLookups } from '../context/TimelineLookupsContext';
import type { WebFormSubmittedPayload } from '../types';
import type { SubItemContent } from '../types/renderer';
import { useEventFormatters } from '../useEventFormatters';
import type { EventComponentProps } from './types';

const FIELD_ICON_CLASS = 'w-4 h-4 text-primary';

export function WebFormSubmittedEvent({
  event,
  entityType,
  isContext,
}: EventComponentProps) {
  const { getActorInfo, getObjectLabel } = useEventFormatters();
  const { lookups } = useViewStateLookups();
  const actor = getActorInfo(event);
  const openSlideout = useOpenSlideoutOptional();
  const payload = event.payload as WebFormSubmittedPayload | undefined;
  const formName = payload?.form_name ?? 'Unknown Form';

  // Resolve the form entity from the entities pool.
  // Prefer form_name → forms index → entities pool; fall back to
  // form_entity_id → entities pool for events that only carry the id.
  const formObject = useMemo(() => {
    if (payload?.form_name) {
      const formEntityId = lookups.forms[payload.form_name];
      if (formEntityId) {
        return lookups.entities[formEntityId];
      }
    }
    if (payload?.form_entity_id) {
      return lookups.entities[payload.form_entity_id];
    }
    return undefined;
  }, [
    payload?.form_name,
    payload?.form_entity_id,
    lookups.forms,
    lookups.entities,
  ]);

  const handleFormClick = useCallback(() => {
    if (formObject) {
      openSlideout?.(String(formObject.entity_id));
    }
  }, [openSlideout, formObject]);

  const formBadgeState = useMemo(
    () =>
      formObject
        ? {
            kind: 'data' as const,
            data: {
              id: String(formObject.entity_id),
              entityType: formObject.entity_type_name,
              displayName: formObject.ahoy_display_name ?? formName,
            },
          }
        : null,
    [formObject, formName]
  );

  const formDisplay = formBadgeState ? (
    <EntityBadge
      state={formBadgeState}
      className='align-middle'
      onClick={openSlideout ? handleFormClick : undefined}
    />
  ) : (
    bold(formName)
  );

  // Check if this is an associated event (from a different entity)
  const isAssociatedEvent = 'association_type_name' in event;
  const associatedObject = isAssociatedEvent
    ? lookups.entities[event.entity_id]
    : undefined;

  const entityLabel = entityType
    .replace(/^ahoy_/, '')
    .replace(/_entity$/, '')
    .replace(/_/g, ' ');

  // Context node → the event's entity is not the record being viewed; use its
  // display name (badged below) instead of "this contact".
  const contextEntity = isContext
    ? lookups.entities[event.entity_id]
    : undefined;
  const objectDisplayText = isAssociatedEvent
    ? getObjectLabel(event.entity_id)
    : (contextEntity?.ahoy_display_name ?? `this ${entityLabel}`);

  const handleObjectClick = useCallback(() => {
    if (associatedObject) {
      openSlideout?.(String(associatedObject.entity_id));
    }
  }, [openSlideout, associatedObject]);

  const handleEntityClick = useCallback(
    (entityId: string) => openSlideout?.(entityId),
    [openSlideout]
  );

  const objectBadgeState = useMemo(
    () =>
      associatedObject
        ? {
            kind: 'data' as const,
            data: {
              id: String(associatedObject.entity_id),
              entityType: associatedObject.entity_type_name,
              displayName: associatedObject.ahoy_display_name ?? 'Unknown',
            },
          }
        : null,
    [associatedObject]
  );

  let objectDisplay: ReactNode;
  if (isAssociatedEvent && objectBadgeState) {
    objectDisplay = (
      <EntityBadge
        state={objectBadgeState}
        className='align-middle'
        onClick={openSlideout ? handleObjectClick : undefined}
      />
    );
  } else if (isAssociatedEvent) {
    objectDisplay = bold(objectDisplayText);
  } else {
    // Primary node → "this contact"; context node → badge the subject entity.
    objectDisplay = (
      <SubjectReference
        isContext={isContext}
        entityId={event.entity_id}
        fallback={objectDisplayText}
        onEntityClick={openSlideout ? handleEntityClick : undefined}
      />
    );
  }

  const visibleFields = useMemo(
    () => (payload?.fields ?? []).filter(f => f.name && f.value),
    [payload?.fields]
  );

  const subItems: SubItemContent[] = useMemo(
    () =>
      visibleFields.map(field => {
        const displayName = field.label || field.name;
        return {
          icon: <Type01 className={FIELD_ICON_CLASS} />,
          text: (
            <span>
              {displayName}: {field.value}
            </span>
          ),
          ariaLabel: `${displayName}: ${field.value}`,
        };
      }),
    [visibleFields]
  );

  const ariaLabel = `${actor.name} captured a submission from ${formName} on ${objectDisplayText}`;

  return (
    <TimelineEvent ariaLabel={ariaLabel}>
      <TimelineEvent.Icon event={event} />
      <TimelineEvent.Content
        eventId={event.event_id}
        header={
          <InlineText>
            <ActorName actor={actor} /> captured a submission from {formDisplay}{' '}
            on {objectDisplay}
          </InlineText>
        }
        timestamp={event.occurred_at}
        subItems={subItems}
      />
    </TimelineEvent>
  );
}
